import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

import { registerUser } from '../../api/BackendApi.jsx';
import { AuthLayout, FormInput, FormButton } from '../../components/AuthComponents.jsx';

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    let timer = null;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (cooldown > 0) {
        return;
    }

    // --- NAŠE VLASTNÍ VALIDACE ---

    // 1. Kontrola emailu
    if (!email.trim()) {
        setError('Zadejte prosím emailovou adresu.');
        return;
    }
    // Volitelně: Jednoduchá kontrola formátu emailu (zda obsahuje @ atd.)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        setError('Zadejte platnou emailovou adresu.');
        return;
    }

    // 2. Kontrola hesla
    if (!password) {
        setError('Zadejte prosím heslo.');
        return;
    }
    if (password.length < 8) {
        setError('Heslo musí mít alespoň 8 znaků.');
        return;
    }
    if (password !== passwordConfirm) {
        setError('Hesla se neshodují.');
        return;
    }
    
    // 3. Kontrola souhlasu s podmínkami
    if (!termsAccepted) {
        setError('Pro registraci musíte souhlasit s obchodními podmínkami a zásadami zpracování osobních údajů.');
        return;
    }

    // ------------------------------

    setIsLoading(true);

    try {
        const response = await registerUser({ email, password });
        setSuccess(response.message || 'Aktivační email byl odeslán. Zkontrolujte Vaši schránku. Tato operace může trvat několik minut.');
    } catch (err) {
        if (err.remaining_seconds) {
            setCooldown(err.remaining_seconds);
            setError(`Pro znovuodeslání emailu počkejte ${err.remaining_seconds} sekund.`);
        } else {
            setError(err.message || 'Registrace se nezdařila. Zkuste to prosím znovu.');
        }
    } finally {
        setIsLoading(false);
    }
  };

    return (
        <AuthLayout title="Vytvořit nový účet">
          {/* PŘIDÁN ATRIBUT noValidate PRO VYPNUTÍ HLÁŠEK PROHLÍŽEČE */}
          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            
            {error && (
              <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 text-sm text-green-700 bg-green-100 rounded-md">
                {success}
              </div>
            )}
            
            <FormInput
              id="email"
              label="Emailová adresa"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Heslo (alespoň 8 znaků)
              </label>

              <div className="relative mt-1">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="password-confirm"
                className="block text-sm font-medium text-gray-700"
              >
                Potvrďte heslo
              </label>

              <div className="relative mt-1">
                <input
                  id="password-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 focus:outline-none"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="text-gray-600">
                  Souhlasím s{' '}
                  <Link 
                    to="/obchodni-podminky" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                  >
                    obchodními podmínkami
                  </Link>
                  {' '}a{' '}
                  <Link 
                    to="/ochrana-osobnich-udaju" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                  >
                    zásadami zpracování osobních údajů
                  </Link>.
                </label>
              </div>
            </div>

            <FormButton 
              isLoading={isLoading} 
              disabled={cooldown > 0}
            >
              {cooldown > 0 
                ? `Zkuste znovu za ${cooldown} sekund` 
                : 'Registrovat'}
            </FormButton>
          </form>

          <p className="text-sm text-center text-gray-600">
            Již máte účet?{' '}
            <Link
              to="/login"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Přihlaste se
            </Link>
          </p>
        </AuthLayout>
    );
};

export default RegisterPage;